import { AchievementHistory } from '../components/AchievementHistory';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { UnifiedSelect } from '../components/UnifiedSelect';
import { Trophy, Crown, ChevronRight, Award, Lock, Check, Star, Pencil } from 'lucide-react';
import { api, json } from '../api';
import { usePlatform } from '../store';
import { UserAvatar } from '../components/UserAvatar';
import { canViewPlayerRankings } from '../../shared/player-rankings';
import './achievements.css';
type Item = {
  blocked: boolean;
  status?: string;
  hidden: boolean;
  key: string;
  id: string;
  name: string;
  description: string;
  gameId: string;
  gameName: string;
  target: number;
  value: number;
  points: number;
  icon: number;
  badge: string;
  title: string;
  unlockedAt: string | null;
  test: boolean;
};
type Profile = {
  items: Item[];
  points: number;
  level: number;
  equipment: { badges: string[]; title: string };
  test: boolean;
};
export function Medal({ icon, locked = false }: { icon: number; locked?: boolean }) {
  return (
    <img
      className={'ach-medal' + (locked ? ' locked' : '')}
      src={'/art/achievements/' + icon + '.webp'}
      alt=""
    />
  );
}
export function Achievements({
  gameId,
  embedded = false,
  inProfile = false,
}: {
  gameId?: string;
  embedded?: boolean;
  inProfile?: boolean;
}) {
  const [params] = useSearchParams();
  const { data } = usePlatform();
  const rankingsAllowed = canViewPlayerRankings(data?.user);
  const [profile, setProfile] = useState<Profile | null>(null),
    [error, setError] = useState(''),
    [tab, setTab] = useState('成就'),
    [game, setGame] = useState(gameId || params.get('game') || ''),
    [status, setStatus] = useState('all'),
    [scope, setScope] = useState('all'),
    [board, setBoard] = useState<
      { id: string; nickname: string; points: number; unlocked: number }[]
    >([]),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    setGame(gameId || params.get('game') || '');
  }, [gameId, params]);
  const load = () =>
    api<Profile>('/achievements')
      .then(setProfile)
      .catch((e) => setError(e.message));
  useEffect(() => {
    if (data?.user) void load();
  }, [data?.user?.id]);
  useEffect(() => {
    if (rankingsAllowed && tab === '排行')
      api<{ items: typeof board }>(
        '/achievements/leaderboard?scope=' + scope + '&game=' + encodeURIComponent(game),
      )
        .then((r) => setBoard(r.items))
        .catch((e) => setError(e.message));
  }, [tab, scope, game, rankingsAllowed]);
  useEffect(() => {
    if (!rankingsAllowed && tab === '排行') {
      setTab('成就');
      setBoard([]);
    }
  }, [rankingsAllowed, tab]);
  async function equip(kind: 'badges' | 'title', key: string) {
    if (!profile || busy) return;
    setBusy(true);
    setError('');
    const e = { ...profile.equipment };
    if (kind === 'title') e.title = e.title === key ? '' : key;
    else e.badges = e.badges.includes(key) ? e.badges.filter((k) => k !== key) : [...e.badges, key];
    if (e.badges.length > 3) {
      setError('最多展示 3 枚徽章');
      setBusy(false);
      return;
    }
    try {
      await api('/achievements/equipment', json('PUT', e));
      setProfile({ ...profile, equipment: e });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (!data?.user)
    return (
      <div className="ach-page">
        <h1>我的成就</h1>
        <p>登录后保存成就，收藏徽章与称号。</p>
        <Link className="button" to="/login?next=%2Fachievements">
          登录查看
        </Link>
      </div>
    );
  if (!profile)
    return (
      <div className="ach-page" role="status">
        {error || '正在打开成就图鉴…'}
      </div>
    );
  const earned = profile.items.filter((i) => i.unlockedAt),
    badges = earned.filter((i) => i.badge),
    titles = earned.filter((i) => i.title);
  const currentTitle = titles.find((i) => i.key === profile.equipment.title);
  const showcase = badges.filter((i) => profile.equipment.badges.includes(i.key));
  const visible = profile.items.filter(
    (i) =>
      (!game || i.gameId === game) &&
      (status === 'all' || (status === 'earned' ? !!i.unlockedAt : !i.unlockedAt)),
  );
  return (
    <section
      className={
        'ach-page' + (embedded ? ' ach-embedded' : '') + (inProfile ? ' ach-in-profile' : '')
      }
      aria-label="我的成就"
    >
      {!embedded && !inProfile && (
        <header className="ach-heading">
          <div>
            <span>MOYU ISLAND / ACHIEVEMENTS</span>
            <h1>我的成就</h1>
          </div>
          {rankingsAllowed && (
            <Link className="ach-rank-link" to="/rankings?group=players">
              <Trophy size={18} />
              成就排行
            </Link>
          )}
        </header>
      )}
      <section className="ach-hero">
        {!inProfile && (
          <div className="ach-person">
            <UserAvatar src={data.user.avatarUrl} size={88} />
            <div>
              <h2>
                {data.user.nickname}
                <small>LV.{profile.level}</small>
              </h2>
              <p>当前称号：{currentTitle?.title || '尚未佩戴'}</p>
              <button onClick={() => setTab('徽章')}>
                <Pencil size={14} />
                编辑展示
              </button>
            </div>
          </div>
        )}
        <div className="ach-growth">
          <div className="ach-growth-label">
            <span>Lv.{profile.level}</span>
            <span>{profile.points % 200} / 200</span>
            <span>Lv.{profile.level + 1}</span>
          </div>
          <progress max={200} value={profile.points % 200} />
          <div className="ach-stats">
            <div>
              <b>{profile.points.toLocaleString()}</b>
              <span>成就积分</span>
            </div>
            <div>
              <b>
                {earned.length}
                <small> / {profile.items.length}</small>
              </b>
              <span>已解锁</span>
            </div>
            <div>
              <b>
                {badges.length}
                <small> 枚</small>
              </b>
              <span>徽章</span>
            </div>
          </div>
        </div>
      </section>
      {error && (
        <p className="ach-error" role="alert">
          {error}
        </p>
      )}
      <div className="ach-layout">
        <section className="ach-library">
          <div className="ach-section-heading">
            <h2>
              <Award />
              成就图鉴
            </h2>
            <span>{visible.length} 项挑战</span>
          </div>
          <div className="ach-tabs" role="tablist">
            {(rankingsAllowed ? ['成就', '徽章', '称号', '排行'] : ['成就', '徽章', '称号']).map(
              (t) => (
                <button
                  role="tab"
                  aria-selected={tab === t}
                  className={tab === t ? 'active' : ''}
                  onClick={() => setTab(t)}
                  key={t}
                >
                  {t}
                </button>
              ),
            )}
          </div>
          <div className="ach-filters">
            <UnifiedSelect
              aria-label="选择游戏"
              value={game}
              onChange={(e) => setGame(e.target.value)}
            >
              <option value="">全部游戏</option>
              {[...new Map(profile.items.map((i) => [i.gameId, i.gameName])).entries()].map(
                ([id, name]) => (
                  <option value={id} key={id}>
                    {name}
                  </option>
                ),
              )}
            </UnifiedSelect>
            {tab === '成就' ? (
              <UnifiedSelect
                aria-label="成就状态"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="all">全部状态</option>
                <option value="progress">未完成</option>
                <option value="earned">已解锁</option>
              </UnifiedSelect>
            ) : tab === '排行' ? (
              <UnifiedSelect
                aria-label="排名范围"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
              >
                <option value="all">全站榜</option>
                <option value="friends">好友榜</option>
              </UnifiedSelect>
            ) : (
              <span></span>
            )}
          </div>
          {tab === '成就' && (
            <div className="ach-grid">
              {visible.map((i) => (
                <article className={'ach-card' + (i.unlockedAt ? ' earned' : '')} key={i.key}>
                  <Medal
                    icon={i.hidden && !i.unlockedAt ? 8 : i.icon}
                    locked={!i.unlockedAt && i.value === 0}
                  />
                  <div className="ach-card-copy">
                    <span className="ach-game">{i.gameName}</span>
                    <h3>{i.name}</h3>
                    <p>{i.description}</p>
                    <div className="ach-reward">
                      <span>
                        <Star size={15} />+{i.points}
                      </span>
                      {i.unlockedAt ? (
                        <em>
                          <Check size={13} />
                          已解锁
                        </em>
                      ) : (
                        <span>
                          {i.blocked
                            ? '已撤销'
                            : i.status === 'disabled'
                              ? '已停用'
                              : `${i.value} / ${i.target}`}
                        </span>
                      )}
                    </div>
                    {!i.unlockedAt && <progress max={i.target} value={i.value} />}
                  </div>
                </article>
              ))}
              {!visible.length && <p className="ach-empty">暂无符合条件的成就</p>}
            </div>
          )}
          {(tab === '徽章' || tab === '称号') && (
            <div className="ach-collection">
              {earned
                .filter((i) => (!game || i.gameId === game) && (tab === '徽章' ? i.badge : i.title))
                .map((i) => (
                  <article key={i.key}>
                    <Medal icon={i.icon} />
                    <h3>{tab === '徽章' ? i.badge : i.title}</h3>
                    <p>
                      {i.gameName} · {i.name}
                    </p>
                    <button
                      disabled={busy}
                      onClick={() => void equip(tab === '徽章' ? 'badges' : 'title', i.key)}
                    >
                      {(
                        tab === '徽章'
                          ? profile.equipment.badges.includes(i.key)
                          : profile.equipment.title === i.key
                      )
                        ? '取消佩戴'
                        : '佩戴'}
                    </button>
                  </article>
                ))}
              {!earned.some(
                (i) => (!game || i.gameId === game) && (tab === '徽章' ? i.badge : i.title),
              ) && <p className="ach-empty">暂无已获得的{tab}</p>}
            </div>
          )}
          {tab === '排行' && (
            <>
              <div
                className="ach-board-label"
                title="部分成就由游戏客户端上报，排名仅供趣味展示，不绑定实际奖励"
              >
                趣味榜
              </div>
              <div className="ach-ranking">
                {board.map((u, n) => (
                  <div key={u.id}>
                    <b>{n + 1}</b>
                    <span>
                      {u.nickname || '岛上玩家'}
                      <small>{u.unlocked} 项成就</small>
                    </span>
                    <strong>{u.points} 分</strong>
                  </div>
                ))}
                {!board.length && <p className="ach-empty">暂时没有排名记录</p>}
              </div>
            </>
          )}
        </section>
        <aside className="ach-honors">
          <section>
            <div className="ach-section-heading">
              <h2>
                <Crown />
                我的荣誉
              </h2>
              <button onClick={() => setTab('徽章')}>
                管理
                <ChevronRight size={15} />
              </button>
            </div>
            <div className="ach-showcase">
              {showcase.map((i) => (
                <div key={i.key}>
                  <Medal icon={i.icon} />
                  <strong>{i.badge}</strong>
                </div>
              ))}
              {!showcase.length && (
                <div className="ach-empty">
                  <Medal icon={6} locked />
                  <p>{badges.length ? '未选择展示徽章' : '暂无徽章'}</p>
                </div>
              )}
            </div>
            <button className="ach-wide" onClick={() => setTab('徽章')}>
              管理徽章
            </button>
          </section>
          <section>
            <h2>
              <Crown />
              我的称号
            </h2>
            <div className="ach-title">
              <Crown size={26} />
              <strong>{currentTitle?.title || '尚未佩戴称号'}</strong>
            </div>
            <button className="ach-wide" onClick={() => setTab('称号')}>
              更换称号
            </button>
          </section>
          {rankingsAllowed && (
            <section>
              <h2>
                <Trophy />
                成就排行
              </h2>

              {embedded ? (
                <button className="ach-wide" onClick={() => setTab('排行')}>
                  查看趣味榜 <ChevronRight size={16} />
                </button>
              ) : (
                <Link className="ach-wide ach-rank-link" to="/rankings?group=players">
                  查看玩家榜 <ChevronRight size={16} />
                </Link>
              )}
            </section>
          )}
        </aside>
      </div>
      <AchievementHistory />
    </section>
  );
}
